import { useState, useCallback, useRef } from 'react';
import { useClearStaleEmrAttemptWhenPushFailedReleased } from './useClearStaleEmrAttemptWhenPushFailedReleased';
import {
  buildUploadFailureResult,
  buildUploadCompletedFields,
  useKinnserUploadStatusPolling,
  type KinnserUploadResultFields,
} from './useKinnserUploadStatusPolling';
import { autoAddInKinnser as autoAddInKinnserEndpoint } from '../../services/api';
import type { AutoAddInKinnserResponse } from '../../services/api';
import {
  getSurfaceableError,
  type SurfaceableResultFields,
} from '../../utils/apiErrorDetail';
import type {
  KinnserUploadJobSummary,
  KinnserUploadStatusResponse,
} from '../../services/api/types.gen';

export type KinnserAutoAddFlowResult = (
  AutoAddInKinnserResponse | { success: false; message: string }
) &
  SurfaceableResultFields &
  KinnserUploadResultFields;

const GENERIC_AUTO_ADD_FAILURE = 'Auto-add failed. Please try again.';

interface UseKinnserAutoAddFlowOptions {
  intakeId: string | undefined;
  /** Global intake status; used to clear stale client errors after push_failed is reset. */
  intakeStatus?: string | null;
  /** `intake.kinnser_upload_job`; resumes an in-flight upload poll after a reload. */
  kinnserUploadJob?: KinnserUploadJobSummary | null;
  getValues: () => {
    kinnser_clinician_id?: string | null;
    /** Current Start of Care date from form (used at schedule click to avoid stale intake-derived value). */
    kinnser_soc_date?: string | null;
    /** Current Episode start date from form (used at schedule click to avoid stale intake-derived value). */
    kinnser_episode_start_date?: string | null;
  };
}

interface UseKinnserAutoAddFlowReturn {
  autoAddInKinnser: boolean;
  autoAddResult: KinnserAutoAddFlowResult | null;
  handleAutoAddInKinnser: () => Promise<void>;
}

/**
 * Hook for SOC "new admission + episode" or "new episode" flow in Kinnser.
 *
 * The backend encapsulates the multi-step flow (auto-add → upload → update details)
 * and records push_failed / Slack notification for 5xx/unexpected failures.
 * When the backend starts the upload asynchronously (`status: "processing"`),
 * the result is set only after the upload status poll finishes.
 */
export function useKinnserAutoAddFlow({
  intakeId,
  intakeStatus,
  kinnserUploadJob,
  getValues,
}: UseKinnserAutoAddFlowOptions): UseKinnserAutoAddFlowReturn {
  const [autoAddInKinnser, setAutoAddInKinnser] = useState(false);
  const [autoAddResult, setAutoAddResult] =
    useState<KinnserAutoAddFlowResult | null>(null);

  const clearAutoAddResult = useCallback(() => {
    setAutoAddResult(null);
  }, []);
  useClearStaleEmrAttemptWhenPushFailedReleased(
    intakeId,
    intakeStatus,
    clearAutoAddResult
  );
  // The POST response of an upload still processing; null after a reload.
  const processingResponseRef = useRef<AutoAddInKinnserResponse | null>(null);

  const handleUploadDone = useCallback(
    (status: KinnserUploadStatusResponse) => {
      const started = processingResponseRef.current;
      processingResponseRef.current = null;
      if (status.status !== 'completed') {
        setAutoAddResult(
          buildUploadFailureResult(status, GENERIC_AUTO_ADD_FAILURE)
        );
        return;
      }
      setAutoAddResult({
        success: true,
        message: 'Auto-added and scheduled in Kinnser successfully.',
        ...buildUploadCompletedFields(status, started),
        soc_patient_task_key:
          status.job?.soc_patient_task_key ?? started?.soc_patient_task_key,
        soc_task_name: status.job?.soc_task_name ?? started?.soc_task_name,
      });
    },
    []
  );

  const { pollingUpload, pollUploadStatus } = useKinnserUploadStatusPolling({
    intakeId,
    intakeStatus,
    flow: 'auto_add',
    storedJob: kinnserUploadJob,
    onDone: handleUploadDone,
  });

  const handleAutoAddInKinnser = useCallback(async () => {
    setAutoAddInKinnser(true);
    setAutoAddResult(null);

    try {
      if (!intakeId?.trim()) {
        setAutoAddResult({ success: false, message: 'No intake selected' });
        return;
      }

      const formValues = getValues();
      const clinicianId =
        formValues.kinnser_clinician_id &&
        typeof formValues.kinnser_clinician_id === 'string' &&
        formValues.kinnser_clinician_id.trim() !== ''
          ? formValues.kinnser_clinician_id.trim()
          : null;

      // Use current form values at click time so we send what the user sees (e.g. Start of Care date).
      // Intake-derived props can be stale if the user edited the form before save/refetch.
      const formSocRaw = formValues.kinnser_soc_date;
      const formSoc =
        formSocRaw && typeof formSocRaw === 'string' && formSocRaw.trim() !== ''
          ? formSocRaw.trim().split('T')[0]
          : null;
      const formEpisodeRaw = formValues.kinnser_episode_start_date;
      const formEpisode =
        formEpisodeRaw &&
        typeof formEpisodeRaw === 'string' &&
        formEpisodeRaw.trim() !== ''
          ? formEpisodeRaw.trim().split('T')[0]
          : null;

      const response = await autoAddInKinnserEndpoint({
        path: { intake_id: intakeId },
        body: {
          soc_date: formSoc ?? undefined,
          episode_start_date: formEpisode ?? undefined,
          clinician_id: clinicianId ?? undefined,
        },
      });

      if (response.data?.status === 'processing') {
        // The episode and task exist; the documents are still uploading.
        processingResponseRef.current = response.data;
        await pollUploadStatus();
      } else if (response.data) {
        setAutoAddResult(response.data);
      } else {
        const surfaceable = getSurfaceableError(response);
        setAutoAddResult(
          surfaceable
            ? {
                success: false,
                message: surfaceable.message,
                surfaceable: true,
                error_code: surfaceable.errorCode,
              }
            : {
                success: false,
                message: GENERIC_AUTO_ADD_FAILURE,
              }
        );
      }
    } catch (error) {
      console.error('Failed to auto-add in Kinnser:', error);
      const surfaceable = getSurfaceableError(error);
      setAutoAddResult(
        surfaceable
          ? {
              success: false,
              message: surfaceable.message,
              surfaceable: true,
              error_code: surfaceable.errorCode,
            }
          : {
              success: false,
              message: 'An unexpected error occurred. Please try again.',
            }
      );
    } finally {
      setAutoAddInKinnser(false);
    }
  }, [intakeId, getValues, pollUploadStatus]);

  return {
    autoAddInKinnser: autoAddInKinnser || pollingUpload,
    autoAddResult,
    handleAutoAddInKinnser,
  };
}
